/**
 * Widget Analytics — lightweight Mixpanel wrapper for the Vite-built chat widget.
 *
 * Mixpanel config (token, enabled) is provided at runtime via the chat-config
 * API endpoint, so no VITE_MIXPANEL_* build-time env vars are needed for deployment.
 *
 * Common properties sent with EVERY event:
 *   - source: 'frontend'
 *   - institutionId (engageId)
 *   - institutionName (from chat-config)
 *   - userId (sessionId)
 *   - pageUrl
 *   - launcherMx, launcherMy, launcherZIndex (from embed script style attribute)
 *   - chatSurface ('floating' launcher widget or 'embedded' inline panel)
 *   - widgetVersion (build commit SHA, omitted when unavailable)
 *
 * IP / Country: Mixpanel captures these automatically from the client request.
 */
import mixpanel from 'mixpanel-browser';
import { getPageUrl } from './visitor-context';
import { getWidgetVersion } from './widget-version';

let initialized = false;

/** Common properties attached to every widget event */
let commonProps: Record<string, unknown> = {};

/** Which chat UI sends the events, so embedded and floating can be compared. */
export type ChatSurface = 'floating' | 'embedded';

/**
 * Initialize Mixpanel for the widget.
 * Call once when the widget config has loaded.
 *
 * @param options.token - Mixpanel project token (from chat-config API)
 * @param options.enabled - Whether analytics is enabled (from chat-config API)
 * @param options.engageId - Institution / engage ID
 * @param options.institutionName - Institution display name (from chat-config)
 * @param options.sessionId - Visitor session ID (from localStorage)
 * @param options.launcherMx - Horizontal launcher offset from embed style (px)
 * @param options.launcherMy - Vertical launcher offset from embed style (px)
 * @param options.launcherZIndex - Launcher stacking layer from embed style
 * @param options.chatSurface - Chat UI sending the events (default 'floating')
 */
export function initWidgetAnalytics(options: {
  token: string;
  enabled: boolean;
  engageId?: string;
  institutionName?: string | null;
  sessionId?: string;
  launcherMx?: number;
  launcherMy?: number;
  launcherZIndex?: number;
  chatSurface?: ChatSurface;
}): void {
  const {
    token,
    enabled,
    engageId,
    institutionName,
    sessionId,
    launcherMx,
    launcherMy,
    launcherZIndex,
    chatSurface = 'floating',
  } = options;

  if (initialized) return;

  if (!enabled || !token) {
    return;
  }

  try {
    mixpanel.init(token, {
      track_pageview: false,
      persistence: 'localStorage',
    });

    const widgetVersion = getWidgetVersion();

    // Set common properties sent with every event
    commonProps = {
      ...(widgetVersion && { widgetVersion }),
      source: 'frontend',
      chatSurface,
      ...(engageId && { institutionId: engageId }),
      ...(institutionName && { institutionName }),
      ...(sessionId && { userId: sessionId }),
      pageUrl: getPageUrl(),
      ...(launcherMx !== undefined && { launcherMx }),
      ...(launcherMy !== undefined && { launcherMy }),
      ...(launcherZIndex !== undefined && { launcherZIndex }),
    };

    // Register as Mixpanel super properties so they're included automatically
    mixpanel.register(commonProps);

    initialized = true;
  } catch {
    // Silently fail — widget should not break if analytics fails
  }
}

/**
 * Track a widget event.
 * Common properties (institutionId, sessionId, pageUrl, etc.) are
 * automatically included via Mixpanel super properties.
 */
export function trackWidgetEvent(
  eventName: string,
  properties: Record<string, unknown> = {}
): void {
  if (!initialized) return;

  try {
    mixpanel.track(eventName, properties);
  } catch {
    // Silently fail — widget should not break if analytics fails
  }
}

export function updateWidgetAnalyticsSessionId(sessionId: string): void {
  if (!initialized) return;
  try {
    commonProps = { ...commonProps, userId: sessionId };
    mixpanel.register({ userId: sessionId });
  } catch {
    // Silently fail
  }
}

